
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, Plus, Calendar, Edit } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { getDocumentDisplayTitle } from "@/utils/document-formatter";
import { useToast } from "@/hooks/use-toast";
import { LoadingState } from "@/components/ui/loading-states";

interface Document {
  id: string;
  title: string;
  type: string;
  created_at: string;
  updated_at: string;
}

export function RecentDocuments() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { data, error } = await supabase
        .from('documents')
        .select('id, title, type, created_at, updated_at')
        .eq('user_id', session.user.id)
        .order('updated_at', { ascending: false })
        .limit(6);

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load your documents. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNew = () => {
    navigate("/documents");
  };

  const handleViewDocument = (documentId: string) => {
    navigate(`/documents/${documentId}`);
  };

  if (isLoading) {
    return <LoadingState message="Loading your documents..." />;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 md:mb-6 gap-4">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Recent Documents</h3>
        </div>
        <Button 
          onClick={handleCreateNew}
          className="bg-blue-600 hover:bg-blue-700 text-sm md:text-base"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create New Document
        </Button>
      </div>

      {documents.length === 0 ? (
        <div className="text-center py-8 md:py-12">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-600 mb-2">No documents yet</h4>
          <p className="text-gray-500 mb-6">Create your first strategic document to get started</p>
          <Button onClick={handleCreateNew} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" />
            Create Your First Document
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
          {documents.map((doc) => (
            <Card 
              key={doc.id} 
              className="cursor-pointer hover:shadow-md transition-all border-gray-200 hover:border-blue-300"
              onClick={() => handleViewDocument(doc.id)}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-sm md:text-base font-semibold text-gray-900 line-clamp-2 flex-1">
                    {doc.title}
                  </CardTitle>
                  <Edit className="h-4 w-4 text-gray-400 flex-shrink-0" />
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2">
                  <Badge variant="secondary" className="text-xs">
                    {getDocumentDisplayTitle(doc.type)}
                  </Badge>
                  <div className="flex items-center text-xs text-gray-500">
                    <Calendar className="h-3 w-3 mr-1" />
                    Updated {formatDistanceToNow(new Date(doc.updated_at), { addSuffix: true })}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
